// src/services/reportService.js
import { 
    collection, query, orderBy, getDocs, getDoc, doc, setDoc, 
    updateDoc, deleteDoc, runTransaction, writeBatch, 
    serverTimestamp, increment, Timestamp 
} from 'firebase/firestore';
import { 
    getStorage, ref, uploadBytes, getDownloadURL, deleteObject 
} from 'firebase/storage';
import { db } from '../config/firebase';

const VISITS_COL = 'visits';
const CLIENTS_COL = 'clients';
const PRODUCTS_COL = 'products';
const ACCOUNTING_COL = 'accounting';
const PARAMS_COL = 'parameters';
const COUNTER_COL = 'counters';

export const reportService = {
    /**
     * Carga inicial de datos para el formulario (Clientes, Productos, Parámetros).
     */
    getInitialData: async () => {
        try {
            const [clientsSnap, productsSnap, paramsSnap] = await Promise.all([
                getDocs(query(collection(db, CLIENTS_COL), orderBy('nombre'))),
                getDocs(query(collection(db, PRODUCTS_COL), orderBy('name'))),
                getDoc(doc(db, PARAMS_COL, 'system-settings'))
            ]);

            return {
                clients: clientsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
                products: productsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
                parameters: paramsSnap.exists() ? paramsSnap.data() : {}
            };
        } catch (error) {
            console.error("Error cargando datos iniciales:", error);
            throw error;
        }
    },

    /**
     * Obtiene un informe específico por ID.
     */
    getVisitById: async (visitId) => {
        try {
            const docSnap = await getDoc(doc(db, VISITS_COL, visitId));
            if (docSnap.exists()) return docSnap.data();
            return null;
        } catch (error) {
            console.error("Error obteniendo visita:", error);
            throw error;
        }
    },

    /**
     * Sube fotos a Storage en paralelo.
     */
    uploadPhotos: async (folio, files) => {
        if (!files || files.length === 0) return [];
        
        const storage = getStorage();
        const uploadPromises = files.map(async (file, index) => {
            const refName = `visit-photos/${folio}-${Date.now()}-${index}.jpg`;
            const sRef = ref(storage, refName);
            await uploadBytes(sRef, file);
            const url = await getDownloadURL(sRef);
            return { url, name: refName };
        });

        return Promise.all(uploadPromises);
    },

    /**
     * GUARDA EL INFORME (Crear o Editar).
     */
    saveReport: async ({
        visitId, formData, materialContext, photosContext, signatureData
    }) => {
        try {
            const batch = writeBatch(db);
            let finalFolio = visitId;

            // 1. Generar Folio si es Nuevo
            if (!visitId) {
                const counterRef = doc(db, COUNTER_COL, 'visitCounter');
                finalFolio = await runTransaction(db, async (t) => {
                    const s = await t.get(counterRef);
                    const n = s.exists() ? s.data().current + 1 : 1;
                    t.set(counterRef, { current: n }, { merge: true });
                    return String(n).padStart(7, '0');
                });
            }

            // 2. Subir Fotos Nuevas
            const uploadedPhotos = await reportService.uploadPhotos(finalFolio, photosContext.newFiles);
            const allPhotos = [...photosContext.existingInfos, ...uploadedPhotos];

            // 3. Actualizar Inventario (Stock)
            const currentQuantities = {};
            materialContext.current.forEach(m => {
                if (m.productId) currentQuantities[m.productId] = (currentQuantities[m.productId] || 0) + parseFloat(m.cantidad || 0);
            });

            const originalQuantities = {};
            materialContext.original.forEach(m => {
                if (m.productId) originalQuantities[m.productId] = (originalQuantities[m.productId] || 0) + parseFloat(m.cantidad || 0);
            });

            const allProductIds = new Set([...Object.keys(currentQuantities), ...Object.keys(originalQuantities)]);
            
            allProductIds.forEach(pid => {
                const curr = currentQuantities[pid] || 0;
                const orig = originalQuantities[pid] || 0;
                const diff = curr - orig; 

                if (diff !== 0) {
                    const prodRef = doc(db, PRODUCTS_COL, pid);
                    batch.update(prodRef, { stock: increment(-diff) });
                }
            });

            // 4. Actualizar Cliente
            const clientRef = doc(db, CLIENTS_COL, formData.clientId);
            batch.set(clientRef, {
                nombre: formData.clientName,
                rut: formData.clientId,
                direccion: formData.direccion,
                telefono: formData.telefono,
                email: formData.email,
                encargado: formData.encargado,
                updatedAt: serverTimestamp()
            }, { merge: true });

            // 5. Preparar Datos de la Visita
            const visitData = {
                ...formData,
                fecha: typeof formData.fecha === 'string' ? Timestamp.fromDate(new Date(formData.fecha + "T12:00:00")) : formData.fecha,
                materiales: materialContext.current.filter(m => m.descripcion),
                fotos: allPhotos,
                firmaDigital: signatureData,
                updatedAt: serverTimestamp()
            };

            const visitRef = doc(db, VISITS_COL, finalFolio);
            if (visitId) {
                batch.update(visitRef, visitData);
            } else {
                visitData.createdAt = serverTimestamp();
                batch.set(visitRef, visitData);
            }

            // 6. Gestión Contable
            const accRef = doc(db, ACCOUNTING_COL, finalFolio);
            if (formData.billingType === 'Directa') {
                batch.set(accRef, {
                    id: finalFolio,
                    clientName: formData.clientName,
                    clientId: formData.clientId,
                    visitDate: visitData.fecha,
                    totalAmount: 0,
                    paidAmount: 0,
                    balance: 0,
                    status: 'Pendiente por Facturar',
                    origin: 'Informe',
                    createdAt: serverTimestamp()
                }, { merge: true });
            } else {
                batch.delete(accRef);
            }

            // 7. Ejecutar Batch
            await batch.commit();
            return finalFolio;

        } catch (error) {
            console.error("Error en saveReport:", error);
            throw error;
        }
    },

    /**
     * Elimina un informe y sus fotos asociadas.
     */
    deleteVisit: async (visitId, photos = []) => {
        try {
            // 1. Eliminar fotos de Storage (Best effort, no bloquea si falla uno)
            const storage = getStorage();
            if (photos && photos.length > 0) {
                const deletePromises = photos.map(p => {
                    // Intentamos extraer la referencia desde la URL o usamos el nombre si lo guardamos
                    // Asumimos que guardaste 'name' en el objeto foto (como hicimos en saveReport)
                    if (p.name) {
                        const fileRef = ref(storage, p.name);
                        return deleteObject(fileRef).catch(err => console.warn("Foto no encontrada o ya borrada:", p.name));
                    }
                    return Promise.resolve();
                });
                await Promise.all(deletePromises);
            }

            // 2. Eliminar Documento de Visita
            await deleteDoc(doc(db, VISITS_COL, visitId));

            // 3. Eliminar registro contable asociado si existe
            // (Opcional: podrías querer mantenerlo, pero generalmente si borras el informe, borras el cobro automático)
            await deleteDoc(doc(db, ACCOUNTING_COL, visitId)).catch(() => {});

        } catch (error) {
            console.error("Error eliminando visita:", error);
            throw error;
        }
    }
};